import { Modal, Textarea } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import { Button, InputField } from '@xver/components/Atoms';
import { invalidateProjectQueries, useProjectFeedback } from '@xver/hooks/useProjectQueries';
import { getErrorCode } from '@xver/utils/error';
import {
    PROJECT_DESCRIPTION_MAX,
    PROJECT_NAME_MAX,
    validateProjectFields,
} from '@xver/utils/projects';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

interface CreateProjectModalProps {
    opened: boolean;
    onClose: () => void;
}

export default function CreateProjectModal({ opened, onClose }: CreateProjectModalProps) {
    const { t } = useTranslation('projects');
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { errorMessage, notifySuccess } = useProjectFeedback();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const nameRef = useRef<HTMLInputElement>(null);
    const descriptionRef = useRef<HTMLTextAreaElement>(null);

    const { mutate, reset, isPending, error } = useMutation({
        mutationFn: () =>
            Endpoints.v1.Projects.createProject({
                name: name.trim(),
                description: description.trim(),
            }),
        onSuccess: async (project) => {
            notifySuccess(
                t('create.successTitle'),
                t('create.successMessage', { name: project.name })
            );
            onClose();
            await invalidateProjectQueries(queryClient);
            navigate(`/projects/${project.id}`);
        },
        onError: (mutationError) => {
            if (getErrorCode(mutationError) === 'project_name_taken') nameRef.current?.focus();
        },
    });

    useEffect(() => {
        if (!opened) return;
        setName('');
        setDescription('');
        setSubmitted(false);
        reset();
    }, [opened, reset]);

    const errors = validateProjectFields({ name, description });
    const nameTaken = getErrorCode(error) === 'project_name_taken';
    const nameError =
        submitted && errors.name
            ? t(`form.errors.name.${errors.name}`, { max: PROJECT_NAME_MAX })
            : nameTaken
              ? errorMessage(error)
              : undefined;
    const descriptionError =
        submitted && errors.description
            ? t('form.errors.descriptionTooLong', { max: PROJECT_DESCRIPTION_MAX })
            : undefined;
    const formError = error && !nameTaken ? errorMessage(error) : undefined;

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (isPending) return;
        setSubmitted(true);
        if (errors.name) return nameRef.current?.focus();
        if (errors.description) return descriptionRef.current?.focus();
        mutate();
    };

    const handleClose = () => {
        if (!isPending) onClose();
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            centered
            size="lg"
            closeOnClickOutside={!isPending}
            closeOnEscape={!isPending}
            closeButtonProps={{ 'aria-label': t('common.close'), disabled: isPending }}
            title={<span className="text-lg font-bold">{t('create.title')}</span>}
        >
            <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
                <p className="text-sm text-fg/78">{t('create.intro')}</p>
                <InputField
                    ref={nameRef}
                    data-autofocus
                    size="md"
                    label={t('form.nameLabel')}
                    description={t('form.nameDescription', { max: PROJECT_NAME_MAX })}
                    value={name}
                    onChange={(event) => {
                        setName(event.target.value);
                        if (nameTaken) reset();
                    }}
                    required
                    autoComplete="off"
                    readOnly={isPending}
                    error={nameError}
                />
                <Textarea
                    ref={descriptionRef}
                    label={t('form.descriptionLabel')}
                    description={t('form.descriptionDescription', { max: PROJECT_DESCRIPTION_MAX })}
                    value={description}
                    onChange={(event) => setDescription(event.currentTarget.value)}
                    autosize
                    minRows={3}
                    readOnly={isPending}
                    error={descriptionError}
                />
                {formError && (
                    <p role="alert" className="text-sm text-fg">
                        {formError}
                    </p>
                )}
                <div className="flex flex-wrap justify-end gap-2 mt-2">
                    <Button variant="secondary" onClick={handleClose} disabled={isPending}>
                        {t('common.cancel')}
                    </Button>
                    <Button type="submit" isLoading={isPending}>
                        {t('create.submit')}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
